/**
 * @file CorsProxyService.js
 * @description Servicio para gestionar solicitudes a través de proxies CORS
 * con soporte para múltiples estrategias y manejo avanzado de CORS.
 * Implementa patrón Chain of Responsibility y principios SOLID.
 * @version 3.1.0
 * @author Nubelytics Engineering Team
 */

import axios from 'axios';
import { isSafeDomain } from './CorsProxyConfig';
import userDataService from '../../services/userDataService';

/**
 * Verifica si la URL pertenece a un dominio de Nubelytics
 * @param {string} url - URL a verificar
 * @returns {boolean} - True si es un dominio de Nubelytics
 */
const isNubelyticsDomain = (url) => {
  if (!url || typeof url !== 'string') return false;
  return [
    'nubelytics.com',
    'api.nubelytics.com',
    'sync.nubelytics.com',
    'data.nubelytics.com',
    'connect.nubelytics.com',
    'auth.nubelytics.com',
    'analytics.nubelytics.com'
  ].some(domain => url.includes(domain));
};

/**
 * Verifica si la URL pertenece a un dominio de terceros con problemas conocidos de CORS
 * @param {string} url - URL a verificar
 * @returns {boolean} - True si es un dominio con problemas conocidos
 */
const isThirdPartyWithKnownCors = (url) => {
  if (!url || typeof url !== 'string') return false;
  return [
    'api.tiendanube.com',
    'api.mercadolibre.com',
    'api.mercadopago.com',
    'api.vtex.com',
    'api.shopify.com',
    'api.woocommerce.com',
    'api.bigcommerce.com'
  ].some(domain => url.includes(domain));
};

/**
 * Verifica si la URL es localhost o una dirección IP local
 * @param {string} url - URL a verificar
 * @returns {boolean} - True si es localhost o IP local
 */
const isLocalhost = (url) => {
  if (!url || typeof url !== 'string') return false;
  return url.includes('localhost') || 
         url.includes('127.0.0.1') || 
         url.includes('::1') || 
         url.match(/192\.168\.\d{1,3}\.\d{1,3}/);
};

// === Estrategia base ===
class CorsProxyStrategy {
  isApplicable(url, options = {}) { return false; }
  process(url, options = {}) { return { url, options }; }
}

// === Estrategia NubelyticsProxy (Firebase Functions) ===
class NubelyticsProxyStrategy extends CorsProxyStrategy {
  constructor() {
    super();
    
    // === Resolver la URL base de la función ===
    // 1. Variable de entorno (ideal para producción con dominio propio)
    let functionBaseUrl = process.env.REACT_APP_FUNCTIONS_BASE_URL;

    // 2. Si estamos en Firebase Hosting, derivar dominio de proyecto (compatibilidad legacy v1)
    if (!functionBaseUrl && typeof window !== 'undefined' && window.__FIREBASE_CONFIG__) {
      try {
        const { projectId = 'nubelytics', region = 'us-central1' } = window.__FIREBASE_CONFIG__;
        functionBaseUrl = `https://${region}-${projectId}.cloudfunctions.net`;
      } catch (e) {
        console.warn('[CORS] Error leyendo __FIREBASE_CONFIG__:', e);
      }
    }

    // 3. Fallback definitivo a la nueva Cloud Run (v2) pública
    if (!functionBaseUrl) {
      functionBaseUrl = '/proxy';
    }

    // === Construir prefijo de proxy ===
    // Si la base ya apunta directamente a la función (run.app o contiene "corsProxy")
    const baseLower = functionBaseUrl.toLowerCase();
    // === Construir prefijo de proxy ===
    let proxyPrefix;

    const hasExplicitPath =
      baseLower.endsWith('/corsproxy') ||
      baseLower.includes('/corsproxy?');

    if (functionBaseUrl === '/proxy') {
      proxyPrefix = '/proxy?url=';
    } else if (baseLower.includes('run.app')) {
      // En Cloud Run el servicio expone la función directamente en la raíz
      proxyPrefix = `${functionBaseUrl}?url=`;
    } else if (hasExplicitPath) {
      // Si ya incluye la ruta de función, no duplicarla
      proxyPrefix = `${functionBaseUrl}?url=`;
    } else {
      // Cloud Functions v1 (cloudfunctions.net) u otros dominios custom necesitan /proxy (v2)
      proxyPrefix = `${functionBaseUrl}/corsProxy?url=`;
    }
    this.proxyUrl = proxyPrefix;
    
    // Headers permitidos en solicitudes que pasarán por el proxy
    this.allowedHeaders = [
      'x-app-version', 'x-monitor-check', 'authorization', 'content-type',
      'x-requested-with', 'accept', 'access-control-request-headers',
      'access-control-request-method', 'x-original-url', 'x-proxy-source',
      'x-proxy-applied', 'x-proxy-reason',
      'x-store-id', 'x_store_id'
    ];
    
    // Headers que deben ser movidos a la URL como parámetros para evitar problemas de preflight
    this.headersToParams = [
      'x-app-version',
      'x-monitor-check',
      'x-sync-version',
      'x-sync-client',
      'x-sync-timestamp'
    ];
    
    // Caché para evitar procesar URLs ya proxied
    this.processedUrlCache = new Map();
  }

  /**
   * Determina si la estrategia es aplicable a esta URL
   * @param {string} url - URL a evaluar
   * @returns {boolean} - true si debe aplicarse la estrategia
   */
  isApplicable(url) {
    if (!url) return false;
    
    // No aplicar si ya es una URL proxied, es localhost o pertenece a dominio seguro
  if (isSafeDomain(url)) return false;
  // No aplicar si ya es una URL proxied o es localhost
    if (url.includes('/corsProxy?url=') || isLocalhost(url)) return false;
    
    // Aplicar siempre para dominios Nubelytics o dominios con problemas CORS conocidos
    if (isNubelyticsDomain(url) || isThirdPartyWithKnownCors(url)) return true;
    
    // Para otras URLs, solo aplicar si son externas (no del mismo origen)
    return url.startsWith('http') && 
           typeof window !== 'undefined' && 
           !url.includes(window.location.hostname);
  }

  /**
   * Procesa la URL aplicando el proxy si corresponde
   * @param {string} url - URL original
   * @param {object} options - Opciones de la solicitud
   * @returns {{url: string, options: object}} - URL procesada y opciones
   */
  process(url, options = {}) {
    if (!this.isApplicable(url)) return { url, options };
    
    // Usar caché si ya procesamos esta URL antes
    const cacheKey = `${url}:${JSON.stringify(options)}`;
    if (this.processedUrlCache.has(cacheKey)) {
      return this.processedUrlCache.get(cacheKey);
    }
    
    // --- Añadir storeId si falta --------------------------------------------------
    try {
      const tmpUrlObj = new URL(url);
      if (!tmpUrlObj.searchParams.has('storeId')) {
        const resolvedStoreId = userDataService?.getStoreId?.();
        if (resolvedStoreId) {
          tmpUrlObj.searchParams.set('storeId', resolvedStoreId);
          url = tmpUrlObj.toString();
          // Asegurarse de enviarlo también como header por compatibilidad
          options.headers = options.headers || {};
          if (!options.headers['x-store-id'] && !options.headers['X-Store-Id']) {
            options.headers['X-Store-Id'] = resolvedStoreId;
          }
        }
      }
    } catch (_) {
      // ignore parse errors
    }

    // Generar una copia profunda de las opciones para evitar mutación
    const newOptions = { 
      ...options, 
      headers: options.headers ? { ...options.headers } : {} 
    };
    
    const encodedUrl = encodeURIComponent(url);
    let proxyUrl = `${this.proxyUrl}${encodedUrl}`;

    // Configuración del proxy con parámetros avanzados para resolver problemas CORS
    const proxyParams = {
      // Parámetros para resolver problemas específicos de CORS
      allow_credentials: true,           // Habilitar credenciales en solicitudes cross-origin
      single_origin: true,               // Forzar header Access-Control-Allow-Origin único
      strict_origin: true,               // Validación estricta de origen
      force_cors_headers: true,          // Forzar headers CORS correctos
      sanitize_response_headers: true,   // Eliminar headers problemáticos
      
      // Información sobre la solicitud
      x_original_url: url,
      x_proxy_source: 'webapp_v2',
      app_version: process.env.REACT_APP_VERSION || '0.0.0',
      allowed_headers: this.allowedHeaders.join(',')
    };

    // Mover TODOS los headers problemáticos a parámetros para evitar problemas de preflight
    this.headersToParams.forEach(header => {
      if (newOptions.headers && newOptions.headers[header]) {
        // Convertir nombre de header a formato de parámetro
        const paramName = header.replace(/^x-/, '').replace(/-/g, '_');
        proxyParams[paramName] = newOptions.headers[header];
        delete newOptions.headers[header];
      }
    });

    // Configuraciones específicas por dominio para resolver casos particulares
    const domain = this._extractDomain(url);
    if (domain.includes('api.nubelytics.com')) {
      // Configuración específica para API principal
      proxyParams.api_domain = true;
      proxyParams.disable_www_rewrite = true;
      proxyParams.remove_redundant_origins = true;
      proxyParams.fix_multiple_origins = true;  // Arreglar problema de múltiples orígenes
      proxyParams.preflight_fix = true;         // Arreglar problema de preflight
    } else if (domain.includes('sync.nubelytics.com')) {
      // Configuración específica para API de sincronización
      proxyParams.sync_domain = true;
      proxyParams.disable_www_rewrite = true;
      proxyParams.fix_multiple_origins = true;  // Arreglar problema de múltiples orígenes
      proxyParams.additional_headers = ['x-sync-version', 'x-sync-client', 'x-sync-timestamp'].join(',');
    } else if (domain.includes('auth.nubelytics.com')) {
      // Configuración específica para API de autenticación
      proxyParams.auth_domain = true;
      proxyParams.always_allow_credentials = true;
      proxyParams.fix_multiple_origins = true;  // Arreglar problema de múltiples orígenes
    } else if (isThirdPartyWithKnownCors(url)) {
      // Configuración para APIs de terceros conocidas
      proxyParams.third_party_api = true;
      proxyParams.fix_multiple_origins = true;
      proxyParams.allow_specific_header = 'x-app-version';
    }

    // Añadir información avanzada de método para prevenir errores de preflight
    if (options.method) {
      proxyParams.request_method = options.method.toUpperCase();
      
      // Para POST/PUT/DELETE/PATCH, asegurar que el preflight sea correcto
      if (['POST', 'PUT', 'DELETE', 'PATCH'].includes(options.method.toUpperCase())) {
        proxyParams.ensure_preflight = true;
      }
    }

    // Añadir todos los parámetros a la URL
    Object.entries(proxyParams).forEach(([k, v]) => {
      if (v === undefined || v === null) return;
      proxyUrl += `&${encodeURIComponent(k)}=${encodeURIComponent(v)}`;
    });

    // En desarrollo o para diagnóstico, guardar historial de proxies
    if (typeof window !== 'undefined' && 
        (process.env.NODE_ENV === 'development' || 
        (window?.location?.hostname || '').includes('nubelytics.com'))) {
      try {
        const hist = JSON.parse(localStorage.getItem('nubelytics_cors_proxy_history') || '[]');
        hist.unshift({ 
          timestamp: new Date().toISOString(), 
          original: url, 
          proxied: proxyUrl,
          method: options.method || 'GET',
          params: { ...proxyParams }
        });
        localStorage.setItem('nubelytics_cors_proxy_history', JSON.stringify(hist.slice(0, 50)));
      } catch (e) { /* Ignorar errores de localStorage */ }
    }
    
    // Configurar withCredentials para garantizar envío de cookies si es necesario
    if (isNubelyticsDomain(url)) {
      newOptions.withCredentials = true;
    }

    // Almacenar en caché para reutilizar
    const result = { url: proxyUrl, options: newOptions };
    this.processedUrlCache.set(cacheKey, result);
    
    return result;
  }
  
  /**
   * Extrae el dominio de una URL
   * @private
   * @param {string} url - URL completa
   * @returns {string} - Dominio extraído
   */
  _extractDomain(url) {
    try {
      const urlObj = new URL(url);
      return urlObj.hostname;
    } catch (e) {
      // Si no es una URL válida, intentar extraer el dominio manualmente
      const match = url.match(/^(?:https?:\/\/)?([^/]+)/);
      return match ? match[1] : '';
    }
  }
}

// --- QueryParamBypassStrategy ---
class QueryParamBypassStrategy extends CorsProxyStrategy {
  isApplicable(url) {
    if (!url) return false;
    // No aplicar si ya es una URL de proxy
    if (url.includes('/corsProxy?url=')) return false;
    // No aplicar a localhost
    if (isSafeDomain(url)) return false;
    if (isLocalhost(url)) return false;
    // Siempre aplicar a Nubelytics y dominios con CORS conocidos
    return isNubelyticsDomain(url) || isThirdPartyWithKnownCors(url);
  }
  process(url, options = {}) {
    if (!this.isApplicable(url)) return { url, options };
    const sep = url.includes('?') ? '&' : '?';
    return { url: `${url}${sep}nocors=1`, options };
  }
}

// --- DevModeHeadersStrategy ---
class DevModeHeadersStrategy extends CorsProxyStrategy {
  isApplicable(url) {
    return process.env.NODE_ENV === 'development' && url && url.startsWith('http');
  }
  process(url, options = {}) {
    if (!this.isApplicable(url)) return { url, options };
    const newOptions = { ...options, headers: { ...options.headers } };
    newOptions.headers['x-debug-cors'] = '1';
    return { url, options: newOptions };
  }
}

// --- DirectHeadersFixStrategy ---
class DirectHeadersFixStrategy extends CorsProxyStrategy {
  isApplicable(url) {
    return url && (isNubelyticsDomain(url) || isThirdPartyWithKnownCors(url));
  }
  process(url, options = {}) {
    if (!this.isApplicable(url)) return { url, options };
    const newOptions = { ...options, headers: { ...options.headers } };
    newOptions.headers['accept'] = 'application/json';
    newOptions.headers['x-requested-with'] = 'XMLHttpRequest';
    return { url, options: newOptions };
  }
}

// =================== SERVICIO PRINCIPAL ===================
class CorsProxyService {
  constructor() {
    this.strategies = [
      new NubelyticsProxyStrategy(),
      new QueryParamBypassStrategy(),
      new DevModeHeadersStrategy(),
      new DirectHeadersFixStrategy()
    ];
  }

  /**
   * Procesa la solicitud aplicando la primera estrategia aplicable.
   * @param {string} url
   * @param {object} options
   * @returns {{url: string, options: object}}
   */
  processRequest(url, options = {}) {
    let result = { url, options };
    for (const strategy of this.strategies) {
      if (strategy.isApplicable(result.url, result.options)) {
        result = strategy.process(result.url, result.options);
      }
    }
    return result;
  }

  /**
   * Realiza la solicitud HTTP usando axios, aplicando las estrategias de proxy.
   * @param {string} url
   * @param {object} options
   * @returns {Promise<any>}
   */
  async request(url, options = {}) {
    const { url: proxiedUrl, options: proxiedOptions } = this.processRequest(url, options);
    return axios({ url: proxiedUrl, ...proxiedOptions });
  }

  /**
   * Fuerza la aplicación de NubelyticsProxyStrategy y devuelve la URL resultante.
   * @param {string} url - URL original
   * @param {object} options - Opciones adicionales (headers, method, etc.)
   * @returns {string} - URL procesada (puede ser la misma si la estrategia no aplica)
   */
  applyNubelyticsProxyStrategy(url, options = {}) {
    if (!url) return url;
    const strategy = this.strategies.find(s => s instanceof NubelyticsProxyStrategy);
    if (!strategy) return url;
    if (strategy.isApplicable(url, options)) {
      const { url: proxiedUrl } = strategy.process(url, options);
      return proxiedUrl;
    }
    return url;
  }

  /**
   * Aplica DirectHeadersFixStrategy directamente sobre una configuración Axios.
   * @param {object} config - Objeto config de Axios (debe incluir url)
   * @returns {object} - Config modificado o el original si la estrategia no aplica
   */
  applyDirectHeadersFixStrategy(config = {}) {
    if (!config || !config.url) return config;
    const strategy = this.strategies.find(s => s instanceof DirectHeadersFixStrategy);
    if (!strategy) return config;
    if (strategy.isApplicable(config.url, config)) {
      const { url, options } = strategy.process(config.url, config);
      // Unir opciones resultantes con las originales manteniendo precedencia de las nuevas
      return { ...config, ...options, url };
    }
    return config;
  }
}

// Singleton para uso en toda la aplicación
const corsProxyService = new CorsProxyService();

// (CommonJS export removido para evitar conflictos con ES Modules)

// También exportar como default para ES modules
export default corsProxyService;

// Exportar clases individuales para extensibilidad
export {
  CorsProxyService,
  CorsProxyStrategy,
  NubelyticsProxyStrategy,
  QueryParamBypassStrategy,
  DevModeHeadersStrategy,
  DirectHeadersFixStrategy
};
